"use client";

import { useMemo, useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { stripHtml } from "@/lib/tool-kit/developer/html-strip";

/* ------------------------------------------------------------------ */
/*  Stat card                                                         */
/* ------------------------------------------------------------------ */

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Page Component                                                    */
/* ------------------------------------------------------------------ */

/** English strings. A translated content file overrides these through `ui`. */
export const HTML_STRIPPER_STRINGS = {
  pasteYourHtml: "Paste your HTML",
  pasteHtmlHereEG:
    "Paste HTML here, e.g. <p>Hello <strong>world</strong></p>...",
  inputCharacters: "Input Characters",
  outputCharacters: "Output Characters",
  tagsRemoved: "Tags Removed",
  plainTextOutput: "Plain Text Output",
  copied: "Copied!",
  copyOutput: "Copy Output",
  noTextContentFoundIn: "No text content found in the HTML.",
} as const;

export default function HtmlStripperWidget() {
  const [text, setText] = useState("");
  const [copied, setCopied] = useState(false);

  const result = useMemo(() => stripHtml(text), [text]);

  function handleCopy() {
    if (result) {
      writeToClipboard(result.output).then((copied) => {
        if (!copied) {
          return;
        }

        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      });
    }
  }

  const strings = useToolUi(HTML_STRIPPER_STRINGS);

  const fmt = useToolNumber();

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="html-input"
        >
          {strings.pasteYourHtml}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="html-input"
          onChange={(e) => setText(e.target.value)}
          placeholder={strings.pasteHtmlHereEG}
          value={text}
        />
      </div>
      {/* Stats */}
      {result && (
        <div className="grid grid-cols-3 gap-4">
          <StatCard
            label={strings.inputCharacters}
            value={fmt(result.inputChars)}
          />
          <StatCard
            label={strings.outputCharacters}
            value={fmt(result.outputChars)}
          />
          <StatCard
            label={strings.tagsRemoved}
            value={fmt(result.tagsRemoved)}
          />
        </div>
      )}
      {/* Output */}
      {result && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-foreground text-lg">
              {strings.plainTextOutput}
            </h2>
            <button
              className="rounded-md border border-border bg-background px-3 py-1.5 text-muted-foreground text-sm hover:bg-muted hover:text-foreground"
              onClick={handleCopy}
              type="button"
            >
              {copied ? strings.copied : strings.copyOutput}
            </button>
          </div>
          <div className="min-h-[120px] whitespace-pre-wrap rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed">
            {result.output || (
              <span className="text-muted-foreground">
                {strings.noTextContentFoundIn}
              </span>
            )}
          </div>
        </section>
      )}
    </>
  );
}
